import React, { ComponentProps } from 'react'
import { Image } from 'react-native'

import { styled } from 'nativewind'

import { Stack } from '../layout'
import { colors } from '../tailwind'
import { Text } from '../typography/Typography'
import { stringToHslColor } from '../utils'

const AvatarContainer = styled(Stack, 'items-center justify-center  ', {
  variants: {
    size: {
      small: 'w-4 h-4 rounded-md  p-0.5 ',
      medium: 'w-5 h-5  rounded-lg p-1',
      large: 'w-6 h-6  rounded-[10] p-[8] ',
      extraLarge: 'w-7 h-7 rounded-[10]',
      maxLarge: 'w-28 h-28 rounded-[10]',
    },
  },
  defaultProps: {
    size: 'medium',
    group: false,
  },
  compoundVariants: [
    {
      size: 'small',
      group: true,
      className: '-ml-[10px]',
    },
    {
      size: 'medium',
      group: true,
      className: '-ml-4/2',
    },
    {
      size: 'large',
      group: true,
      className: '-ml-4/2',
    },
  ],
})

const AvatarContent = styled(Text, 'font-bold text-grays-80', {
  variants: {
    size: {
      small: ' text-xs',
      medium: ' text-sm',
      large: ' text-sm',
      extraLarge: 'text-sm',
      maxLarge: 'text-4xl',
    },
    text: {
      true: ' bg-grays-30 text-primary-100',
    },
  },
  defaultProps: {
    size: 'medium',
  },
})

const Frame = styled(
  Stack,
  'border-[#fff]/50 bg-transparent  absolute top-0 bottom-0 right-0 left-0 border-2 ',
  {
    variants: {
      size: {
        small: ' w-4 h-4 rounded-md  ',
        medium: ' w-5 h-5  rounded-lg  ',
        large: ' w-6 h-6 rounded-[10]  ',
        extraLarge: 'w-7 h-7 rounded-[10]  ',
        maxLarge: 'w-28 h-28 rounded-[10]',
      },
    },
    defaultProps: {
      size: 'medium',
    },
  }
)

const DP = styled(Image, {
  variants: {
    size: {
      small: ' rounded-md  ',
      medium: ' rounded-lg ',
      large: ' rounded-[10]  ',
      extraLarge: ' rounded-[10]  ',
      maxLarge: 'rounded-[10]',
    },
  },
  defaultProps: {
    size: 'medium',
  },
})

type AvatarFrameProps = ComponentProps<typeof AvatarContainer>
type AvatarContentProps = ComponentProps<typeof AvatarContent>

type AvatarProps = AvatarFrameProps &
  AvatarContentProps & {
    name: string
    image?: string | null | undefined
    size?: 'small' | 'medium' | 'large' | 'extraLarge' | 'maxLarge' | undefined
    avatarBackground?: string
    remainingCount?: number
    index?: number
    group?: boolean
  }

export default function Avatar({
  name,
  image,
  size,
  group,
  avatarBackground,
  remainingCount,
  index,
  ...props
}: AvatarProps) {
  const hslColor = stringToHslColor(name ?? '', 50, 75)

  const getInitials = (name: string) => {
    const nameArray = name
      .trim()
      .replace(/[^a-zA-Z ]/g, '')
      .split(' ')
    if (nameArray.length === 1) return nameArray[0].charAt(0).toUpperCase()
    else if (nameArray.length > 1) {
      return (
        nameArray[0].charAt(0).toUpperCase() +
        nameArray[1].charAt(0).toUpperCase()
      )
    }
  }

  return (
    <AvatarContainer
      {...props}
      style={{
        backgroundColor:
          remainingCount && index === 3
            ? colors.grays[30]
            : avatarBackground
            ? avatarBackground
            : hslColor,
      }}
      size={size}
      group={group}
      className={image ? 'p-0' : ''}
    >
      {remainingCount && index === 3 ? (
        <AvatarContent text={true} size={size}>
          +{remainingCount}
        </AvatarContent>
      ) : image ? (
        <DP
          size={size}
          source={{ uri: image }}
          style={{
            width: '100%',
            height: '100%',
            resizeMode: 'center',
            overflow: 'hidden',
          }}
        />
      ) : (
        <AvatarContent size={size}>{getInitials(name ?? '')}</AvatarContent>
      )}

      <Frame size={size} />
    </AvatarContainer>
  )
}
